<script setup lang="ts">
  import SkeletonAtom from '@/shared/components/rebranding/SkeletonAtom/SkeletonAtom.vue'
  import { formatNumber } from '@/shared/utils'

  interface Props {
    personCount: number
    quoteInclusivePrice: number
    quoteTotalPrice: number
    inboundAddonPrice: number
    isInboundAddon: boolean
    isInboundAddonSelected: boolean
    loadingPrice: boolean
  }

  defineProps<Props>()
</script>

<template>
  <div class="quote__checkout">
    <ul class="quote__checkout-list">
      <div v-if="isInboundAddon" class="quote__checkout-list-wrapper--inbound">
        <li class="per quote__checkout-list-item text-medium">
          <span class="per__title">
            {{ $t('quote.mandatory_block.header') }}
            <span v-if="personCount > 1">{{ $t('per_person') }}</span>
          </span>
          <span>
            <span v-if="personCount > 1" class="per__person-count">
              x{{ personCount }}&nbsp;&nbsp;
            </span>
            <span class="ltr">
              {{ $t('qic_qar') }} {{ formatNumber(Number(quoteInclusivePrice.toFixed(0))) }}
            </span>
          </span>
        </li>
        <li v-if="isInboundAddonSelected" class="per quote__checkout-list-item text-medium">
          <template v-if="!loadingPrice">
            <span class="per__title">{{ $t('quote.inbound_block.header') }}</span>
            <span>
              <span class="ltr">
                {{ $t('qic_qar') }} {{ formatNumber(Number(inboundAddonPrice.toFixed(0))) }}
              </span>
            </span>
          </template>
          <template v-else>
            <SkeletonAtom :max-width="160" :height="24" />
            <SkeletonAtom :max-width="67" :height="24" />
          </template>
        </li>
      </div>
      <li
        v-if="personCount > 1 && !isInboundAddon"
        class="per quote__checkout-list-item text-medium"
      >
        <span class="per__title">{{ $t('qic_per_person') }}</span>
        <span>
          <span class="per__person-count">x{{ personCount }}&nbsp;&nbsp;</span>
          <span class="ltr">
            {{ $t('qic_qar') }} {{ formatNumber(Number(quoteInclusivePrice.toFixed(0))) }}
          </span>
        </span>
      </li>
      <li class="quote__checkout-list-item total-price">
        <template v-if="!loadingPrice">
          <span class="heading-5">{{ $t('qic_quote_checkout_total') }}</span>
          <span class="heading-5 ltr">
            {{ $t('qic_qar') }} {{ formatNumber(Number(quoteTotalPrice.toFixed(0))) }}
          </span>
        </template>
        <template v-else>
          <SkeletonAtom :max-width="160" :height="24" />
          <SkeletonAtom :max-width="67" :height="24" />
        </template>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
  .quote__checkout {
    background: var(--colorBgSrfTertiary);
    padding: 0 var(--spacing-7);
    border-radius: var(--radius-5);

    .quote__checkout-list {
      border-bottom: none;
      padding: 0;
      gap: 0;

      .quote__checkout-list-item {
        padding-block: var(--spacing-6);
      }

      .quote__checkout-list-value {
        display: flex;
        align-items: center;
      }

      & > *:not(:last-child) {
        border-bottom: 1px solid var(--colorBrdrSrfPrimary);
      }
    }

    .per__title {
      color: var(--colorTxtSrfSecondary);
    }

    .per__person-count {
      color: var(--colorTxtSrfTertiary);
    }

    .ltr {
      display: inline-block;
    }
  }

  .quote__checkout-list-wrapper--inbound {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-3);
    padding-block: var(--spacing-6);

    .quote__checkout-list-item {
      color: var(--colorTxtSrfSecondary);
      padding-block: 0 !important;
    }

    .per__person-count {
      margin-inline-end: 0;
    }
  }
</style>
